import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Link, Paper, Stack, Typography, useTheme, IconButton } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useCallback } from "react";

import { ShowMoreCollapse } from "./ShowMoreCollapse";
import { useFulfillmentDetails } from "./useFulfillmentDetails";

// Constants for identifier field styling
const IDENTIFIER_FIELD_LABEL_MIN_WIDTH = "120px";
const IDENTIFIER_FIELD_LABEL_FONT_SIZE = "12px";
const IDENTIFIER_FIELD_LABEL_LINE_HEIGHT = "12px";
const IDENTIFIER_FIELD_VALUE_FONT_SIZE = "16px";
const IDENTIFIER_FIELD_VALUE_LINE_HEIGHT = "1.5";

// Custom hook for identifier actions (separating business logic from UI)
const useIdentifierActions = () => {
  const alerts = useAlerts();
  const copy = useCopyToClipboard();

  const copyToClipboard = useCallback(
    async (text: string | undefined, fieldName: string) => {
      if (text) {
        await copy(text);
        alerts.info(`${fieldName} copied to clipboard`);
      }
    },
    [copy, alerts]
  );

  return { copyToClipboard };
};

export function ImportantIdentifiers() {
  const { data } = useFulfillmentDetails();
  const theme = useTheme();
  const { copyToClipboard } = useIdentifierActions();
  const [isExpanded, setIsExpanded] = useLocalStorageState<boolean>(
    "fulfillment-important-identifiers-expanded",
    { defaultValue: true }
  );

  return (
    <Stack
      direction="column"
      component={Paper}
      elevation={0}
      sx={{
        padding: theme.spacing(3),
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          mb: 3,
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Typography variant="h6">Important Identifiers</Typography>
        <IconButton size="small" sx={{ ml: 1 }}>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Always visible field */}
      <Stack direction="column" spacing={1}>
        <IdentifierField
          label="Facility ID"
          value={data?.tierIIReport.facility.id}
          onCopy={(value) => copyToClipboard(value, "Facility ID")}
        />
      </Stack>

      <ShowMoreCollapse isExpanded={isExpanded} onExpand={() => setIsExpanded(true)}>
        <Stack direction="column" spacing={1} sx={{ mt: 1 }}>
          <IdentifierField
            label="Report ID"
            value={data?.tierIIReport.id}
            onCopy={(value) => copyToClipboard(value, "Report ID")}
          />

          <IdentifierField
            label="Current Activity ID"
            value={data?.tierIIReport.currentWorkflow?.currentActivityId}
            onCopy={(value) => copyToClipboard(value, "Current Activity ID")}
          />

          <IdentifierField
            label="Workflow ID"
            value={data?.tierIIReport.currentWorkflow?.id}
            onCopy={(value) => copyToClipboard(value, "Workflow ID")}
          />

          <IdentifierField
            label="Tenant ID"
            value={data?.tierIIReport.tenant?.id}
            onCopy={(value) => copyToClipboard(value, "Tenant ID")}
          />

          <IdentifierField
            label="Partner Tenant ID"
            value={data?.tierIIReport.tenant?.partnerTenantId}
            onCopy={(value) => copyToClipboard(value, "Partner Tenant ID")}
          />

          <IdentifierField
            label="PIR Task ARN"
            value={
              data?.tierIIReport.currentWorkflow?.currentActivity?.currentJob?.taskArn ?? "N/A"
            }
            onCopy={(value) => copyToClipboard(value, "PIR Task ARN")}
          />

          <IdentifierField
            label="PIR Task URL"
            value={
              data?.tierIIReport.currentWorkflow?.currentActivity?.currentJob?.taskUrl ?? "N/A"
            }
            onCopy={(value) => copyToClipboard(value, "PIR Task URL")}
            isLink={true}
          />

          <IdentifierField
            label="Reporting Year"
            value={data?.tierIIReport.reportingYear?.toString()}
            onCopy={(value) => copyToClipboard(value, "Reporting Year")}
          />

          <IdentifierField
            label="Facility Customer ID"
            value={data?.tierIIReport.facility.customerFacilityId}
            onCopy={(value) => copyToClipboard(value, "Facility Customer ID")}
          />

          <IdentifierField
            label="Facility Name"
            value={data?.tierIIReport.facility.name}
            onCopy={(value) => copyToClipboard(value, "Facility Name")}
          />

          <IdentifierField
            label="Facility State"
            value={data?.tierIIReport.facility.state}
            onCopy={(value) => copyToClipboard(value, "Facility State")}
          />

          <IdentifierField
            label="Facility County"
            value={data?.tierIIReport.facility.county}
            onCopy={(value) => copyToClipboard(value, "Facility County")}
          />

          <IdentifierField
            label="Facility Address"
            value={data?.tierIIReport.facility.streetAddress1}
            onCopy={(value) => copyToClipboard(value, "Facility Address")}
          />

          <IdentifierField
            label="Facility City"
            value={data?.tierIIReport.facility.city}
            onCopy={(value) => copyToClipboard(value, "Facility City")}
          />

          <IdentifierField
            label="Facility Zip"
            value={data?.tierIIReport.facility.zip}
            onCopy={(value) => copyToClipboard(value, "Facility Zip")}
          />

          <IdentifierField
            label="Organization Name"
            value={data?.tierIIReport.tenant?.name}
            onCopy={(value) => copyToClipboard(value, "Organization Name")}
          />

          <IdentifierField
            label="Partner Tenant Name"
            value={data?.tierIIReport.tenant?.partnerTenant?.name}
            onCopy={(value) => copyToClipboard(value, "Partner Tenant Name")}
          />

          <IdentifierField
            label="Report Status"
            value={data?.tierIIReport.encampStatus}
            onCopy={(value) => copyToClipboard(value, "Report Status")}
          />

          <IdentifierField
            label="Organization Status"
            value={data?.tierIIReport.organizationStatus}
            onCopy={(value) => copyToClipboard(value, "Organization Status")}
          />

          <IdentifierField
            label="Report Kind"
            value={data?.tierIIReport.reportKind}
            onCopy={(value) => copyToClipboard(value, "Report Kind")}
          />

          <IdentifierField
            label="Current Activity Job ID"
            value={data?.tierIIReport.currentWorkflow?.currentActivity?.currentJob?.id}
            onCopy={(value) => copyToClipboard(value, "Current Activity Job ID")}
          />
        </Stack>
      </ShowMoreCollapse>
    </Stack>
  );
}

interface IdentifierFieldProps {
  label: string;
  value: string | null | undefined;
  onCopy: (value: string) => void;
  isLink?: boolean;
}

function IdentifierField({ label, value, onCopy, isLink = false }: IdentifierFieldProps) {
  const theme = useTheme();
  const displayValue = value || "N/A";

  const handleClick = () => {
    onCopy(displayValue);
  };

  return (
    <Stack direction="row" spacing={2} alignItems="baseline">
      <Typography
        variant="caption"
        sx={{
          minWidth: IDENTIFIER_FIELD_LABEL_MIN_WIDTH,
          color: theme.palette.text.secondary,
          fontSize: IDENTIFIER_FIELD_LABEL_FONT_SIZE,
          lineHeight: IDENTIFIER_FIELD_LABEL_LINE_HEIGHT,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1 }}>
        {isLink && value && value !== "N/A" ? (
          <Link
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            sx={{
              fontSize: IDENTIFIER_FIELD_VALUE_FONT_SIZE,
              lineHeight: IDENTIFIER_FIELD_VALUE_LINE_HEIGHT,
              cursor: "pointer",
              wordBreak: "break-all",
              overflowWrap: "break-word",
            }}
          >
            {displayValue}
          </Link>
        ) : (
          <Typography
            variant="body2"
            onClick={handleClick}
            sx={{
              fontSize: IDENTIFIER_FIELD_VALUE_FONT_SIZE,
              lineHeight: IDENTIFIER_FIELD_VALUE_LINE_HEIGHT,
              cursor: "pointer",
            }}
          >
            {displayValue}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
